/**
 * @fsd_page Listing - галерея, список ресторанов с фильтрацией
 *   - all_rests_listing - список всех ресторанов
 *   - search_rests_listing - список ресторанов найденных по поиску
 *   - tag_rests_listing - рестораны по тегу
 */

import s from './Listing.module.sass'

import dynamic from 'next/dynamic'
import { memo, useCallback, useContext, useEffect, useMemo } from 'react'

import { GlobalContext, type TypeGlobalContext } from '@app/context'
import { useAppDispatch, useAppSelector, useInjectPageReducer } from '@app/model/store/client'
import { usePageGlobal } from '@app/PageLayout/pageGlobalContext'

import { ListingDescriptionSlot } from '@pages/Listing/ui/ListingDescriptionPage'
import { loadFullRestaurantForListingCard, loadMorePage, loadPage } from '@pages/Listing/model/thunks'
import { SelectionsSection } from '@pages/Listing/ui/SelectionsSection'
import ListingReducer, { setList, setLoading, setPagination, setParamsRequest } from '@pages/Listing/model/slice'
import type { ListingRestaurant, ListingSeoIntro, ListingState } from '@pages/Listing/model/types'
import type { ReactNode } from 'react'

import { RestCard, RestCardSkeleton } from '@widgets/Restaurant/RestCards/RestCard'

import { Pagination } from '@features/Common/Pagination'
import { RestaurantFilters } from '@features/Restaurant/RestaurantFilters'
import type { TypeOutput } from '@features/Restaurant/RestaurantFilters/model/types'

import { AdvertTemplate, SidebarSticky } from '@shared/ui/Layout/AdvertTemplate'
import { BlockBoundary } from '@shared/lib/components/BlockBoundary'
import { ContainerLongLeft } from '@shared/ui/Layout/Containers/ContainerLongLeft'
import { ContainerTwoColumns } from '@shared/ui/Layout/Containers/ContainerTwoColumns'
import { EGG } from '@shared/api/analytics'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import { useRouterCompat } from '@shared/lib/hooks/useRouterCompat'

const GigAssChatSection = dynamic(
  () => import('@widgets/Common/GigAssist/components/GigAssChatSection').then((module) => module.GigAssChatSection),
  { ssr: false, loading: () => null },
)

type ListItem = { type: 'advert' } | { type: 'restCard'; data: ListingRestaurant }

const KUPER_TOKEN = process.env.KUPER_TOKEN || ''

type BuildKuperAdvertParams = { show: boolean; restName: string; link: string }
type BuildKuperAdvertResult = { kuper: { restName: string; link: string; token: string } } | null

/** @function - формирует объект advert с kuper для RestCard или null */
const buildKuperAdvert = (params: BuildKuperAdvertParams): BuildKuperAdvertResult => {
  const { show, restName, link } = params
  return show ? { kuper: { restName, link, token: KUPER_TOKEN } } : null
}

const COLUMNS_BY_BREAKPOINT: [number, number][] = [
  [1592, 12],
  [541, 12],
  [0, 10],
]

const columnsForWidth = (width: number): number => COLUMNS_BY_BREAKPOINT.find(([min]) => width >= min)?.[1] ?? 10

// на сервере окна нет: mobile уже уже 768, это те же 10 колонок, что и до 541px
const columnsForDesignEnv = (isMobile: boolean): number => columnsForWidth(isMobile ? 375 : 1280)

const isAdvVisible = (currentIndex: number, columns: number): boolean =>
  currentIndex !== 0 && currentIndex % columns === 0

const getListWithAdv = (list: ListingRestaurant[] | null, columns: number): ListItem[] => {
  const result: ListItem[] = []
  const items = list ?? []
  items.forEach((restCard, index) => {
    if (isAdvVisible(index, columns)) result.push({ type: 'advert' })
    result.push({ type: 'restCard', data: restCard })
  })
  return result
}

type ListingRestCardItemProps = {
  data: ListingRestaurant
  index: number
  isSpecialRests: boolean
  className: string
  onVisibleOnce: (restaurantId: number) => void
}

const ListingRestCardItem = memo(function ListingRestCardItem({
  data,
  index,
  isSpecialRests,
  className,
  onVisibleOnce,
}: ListingRestCardItemProps) {
  const kuperLink = 'kuper' in data ? data.kuper?.link : null
  const isShowKuperButton = Boolean(!isSpecialRests && kuperLink)

  const advert = buildKuperAdvert({
    show: isShowKuperButton,
    restName: data.name,
    link: kuperLink ?? '',
  })

  const cardType = isSpecialRests ? 'special-offer' : 'list'
  const cardSizes = isSpecialRests ? 'L' : 'L S'

  return (
    <RestCard
      type={cardType}
      sizes={cardSizes}
      data={data}
      className={className}
      analytic={{
        advertCardPlaceType: 'search',
        section_name: 'Листинг',
        section_index: index,
      }}
      advert={advert}
      onVisibleOnce={() => onVisibleOnce(data.id)}
    />
  )
})

type ListingProps = {
  serverIntro?: ReactNode
  initialIntroView: ListingSeoIntro
  seoIntro?: ListingSeoIntro | null
  initialList?: ListingRestaurant[] | null
  initialPagination?: ListingState['pagination']
  initialSelections?: ListingState['selections']['list']
}

const Listing = ({
  serverIntro,
  initialIntroView,
  initialList = null,
  initialPagination = null,
  initialSelections = null,
}: ListingProps) => {
  useInjectPageReducer('listing', ListingReducer)

  const { isMobile, isTablet } = useClientResponsive()
  const filtersIsReady = useAppSelector((store) => store.features.restaurant_filters.ready)
  const storeListing = useAppSelector((store) => store.pages.listing)
  const hasStoreList = storeListing.isReady && storeListing.list != null
  const list = hasStoreList ? storeListing.list : initialList
  const pagination = hasStoreList ? storeListing.pagination : initialPagination
  const loading = hasStoreList ? storeListing.loading : { page: false, items_full: false, items_addition: false }
  const selections = hasStoreList ? storeListing.selections : { loading: false, list: initialSelections }
  const { paramsRequest } = storeListing
  const dispatch = useAppDispatch()
  const router = useRouterCompat()
  const globalTuple = useContext(GlobalContext)
  const setGlobalContext = globalTuple?.[1]
  const citySlug = usePageGlobal()?.this_city?.slug ?? ''

  const isSpecialRests = !!paramsRequest?.has_special

  // * pagination const *
  const currentPageNumber = pagination?.page.current.number

  const handleLoadingFilter = useCallback((loadingProps: boolean) => {
    dispatch(setLoading({ ...loading, items_full: loadingProps }))
  }, [])

  const handleFilterData = useCallback(
    (data: TypeOutput) => {
      if (!router.query.city_slug || router.query.city_slug === 'undefined') return

      const url = `/${router.query.city_slug}/restaurants/restaurant_list/${data.urlQuery}`
      // программный router.replace — не проходит через клик по <a>, нужно явно включить pending
      setGlobalContext?.((c: TypeGlobalContext) => ({ ...c, isRoutePending: true }))
      router.replace(url)

      dispatch(setParamsRequest(data.paramsRequest))
      dispatch(setList(data.restaurants))
      dispatch(setPagination(data.pagination))
    },
    [router, dispatch, setGlobalContext],
  )

  const getFiltersSlugs = (router: Pick<ReturnType<typeof useRouterCompat>, 'query'>) => {
    const raw = router.query.rests_tags
    if (raw == null) return []
    return Array.isArray(raw) ? raw : [raw]
  }

  useEffect(() => {
    if (filtersIsReady) {
      EGG.pages.Listing.appear_screen_page()
    }
  }, [filtersIsReady, router.asPath])

  const isShowSelections = !isMobile && (selections.loading || (!selections.loading && !!selections.list?.length))

  const loadingRestsList = loading.page || loading.items_full
  const isShowListRests = loadingRestsList || (!loadingRestsList && !!list?.length)

  /** @function - рендер элемента-рекламы в списке */
  const renderAdvertItem = (index: number): React.ReactNode => (
    <div className={s['advertListing']} key={`advert-${index}`} data-listing={'test'}>
      <AdvertTemplate type={'100%x240 Listing1'} count={index} className={s.advertListing} />
    </div>
  )

  const handleRestCardVisibleOnce = useCallback(
    (restaurantId: number) => {
      dispatch(loadFullRestaurantForListingCard(restaurantId))
    },
    [dispatch],
  )

  const listGrid = useMemo(() => {
    const columns = columnsForDesignEnv(isMobile)
    return getListWithAdv(list, columns).map((item, index) => {
      const views = [
        item.type === 'advert' && renderAdvertItem(index),
        item.type === 'restCard' && (
          <ListingRestCardItem
            key={item.data.id}
            data={item.data}
            index={index}
            isSpecialRests={isSpecialRests}
            className={s.card}
            onVisibleOnce={handleRestCardVisibleOnce}
          />
        ),
      ]
      return views.find(Boolean) ?? <></>
    })
  }, [list, isMobile, isSpecialRests, handleRestCardVisibleOnce])

  /** @function - рендер скелетона списка карточек */
  const renderSkeletonView = (): React.ReactNode => (
    <ul key='skeleton' className={s['rest-list-grid']}>
      {Array.from(Array(9).keys()).map((_, idx) => (
        <RestCardSkeleton key={idx} sizes={'L S'} className={s.card} type={'list'} />
      ))}
    </ul>
  )

  /** @function - рендер списка карточек */
  const renderListView = (grid: React.ReactNode): React.ReactNode => (
    <ul key='list' className={s['rest-list-grid']} data-baseline-slot='listing.cards'>
      {grid}
      <li className={s.gigAssist}>
        <GigAssChatSection suggestsAppearance='pink' />
      </li>
    </ul>
  )

  /** @function - рендер пустого состояния */
  const renderEmptyView = (): React.ReactNode => <div key='empty'>По вашему запросу ничего не найдено...</div>

  /** @function - возвращает один из вариантов: скелетоны списка, список карточек или пустое состояние */
  const getListView = (showList: boolean, loading: boolean, grid: React.ReactNode): React.ReactNode => {
    const views = [
      showList && loading && renderSkeletonView(),
      showList && !loading && renderListView(grid),
      !showList && renderEmptyView(),
    ]
    return views.find(Boolean) ?? null
  }

  return (
    <BlockBoundary>
      <ContainerTwoColumns className={s.wrapper}>
        <ContainerTwoColumns.Main>
          <ListingDescriptionSlot serverIntro={serverIntro} initialIntroView={initialIntroView} />

          <RestaurantFilters
            citySlug={citySlug}
            selected={getFiltersSlugs(router)}
            preloadingResults={false}
            handleOutputData={handleFilterData}
            handleLoading={handleLoadingFilter}
            filters={[
              'map',
              'hasBooking',
              'afishaSelected',
              'delivery',
              'openNow',
              'metro',
              'rating',
              'price',
              'kitchen',
              'establishment',
            ]}
          />

          <div className={s['rest-list-layout']} data-is-special={isSpecialRests}>
            {getListView(isShowListRests, loadingRestsList, listGrid)}

            <div
              className={s.pagination}
              data-has-pagination={!!pagination?.page?.count && pagination.page.count > 1}
              data-baseline-slot='listing.pagination'>
              <Pagination
                mode={'all'}
                autoloadingOnMobile={true}
                currentPage={currentPageNumber ?? 0}
                pageCount={pagination?.page?.count ?? 0}
                handleLoadMore={() => {
                  dispatch(loadMorePage())
                }}
                handleSelectPage={(page: number) => {
                  dispatch(loadPage(page))
                }}
                analytic={{ section_name: 'Листинг' }}
              />
            </div>
          </div>

          {!isMobile && !isTablet && <AdvertTemplate type={'Parallax'} className={s['advert-two-columns']} />}
        </ContainerTwoColumns.Main>

        {!isMobile && !isTablet && (
          <ContainerTwoColumns.Aside className={s.columnAdvert}>
            <AdvertTemplate type={'240x400'} container={SidebarSticky} />
            {list?.length >= (isTablet ? 12 : 16) && <AdvertTemplate type={'240x400_2nd'} container={SidebarSticky} />}
          </ContainerTwoColumns.Aside>
        )}
      </ContainerTwoColumns>

      {/*Подборки Афиши Рестораны*/}
      {isShowSelections && (
        <ContainerLongLeft>
          <SelectionsSection selectionsList={selections.list ?? []} skeleton={selections.loading} />
        </ContainerLongLeft>
      )}

      {!isMobile && !isTablet && <AdvertTemplate type={'Content1'} container={ContainerLongLeft} />}
    </BlockBoundary>
  )
}

export default Listing
